import { useContext, useEffect } from 'react';
import { ServiceRequest } from '@/components/Service/SubPages/ServiceRequest';
import { ServiceContext } from '@/context/serviceContext';
import { tabItemNames } from '@/components/Service/TabContainer/tabItems';
import { MyDevices } from '@/components/Service/SubPages/MyDevices';
import { NextPageContext } from 'next';
import { fetchIRAndAssetsDetails } from '@/actions/fetchProduct';
import Error from '@/components/ErrorComponents';
import { BenefitsType } from '@/types/tier';
import { ProductContext } from '@/context/productContext';
import { AssetsData } from '@/types/asset';
import { ServiceRequestContext } from '@/context/serviceRequestContext';
import { getIRWorkOrders, serviceRequestHistory } from '../../actions/fetchServiceHistory';
import { useRouter } from 'next/router';
import { transformServiceHistory } from '@/utils/serviceRequest';
import {
  fetchIRWorkOrderStatus,
  fetchServiceRequestStatus,
} from '@/actions/fetchServiceRequestStatus';
import { checkIfPastReq } from '../../utils/serviceRequest';
import { reqHistoryType } from '@/types/reqHistory';
import PageLayout from '@/components/Layouts/PageLayout';
import Container from '@/shared/Container';
import { getSelectedDevice, getSelectedIRDevice } from '@/utils/amcPlans';
import classNames from 'classnames';
import ssrErrorHandler from '@/errors/ssrErrorHandler';
import { DEVICE_STATUS, GENERIC_ERROR_MESSAGE, IR_WORK_ORDER_QUERY } from '@/constants/app';
import useTriggerRequest from '@/hooks/useTriggerRequest';
import useOnMount from '@/hooks/useOnMount';
import date from '@/utils/date';
import { fetchDispositions } from '@/actions/fetchDispositions';
import useToast from '@/hooks/useToast';
import { getServiceCost } from '@/actions/fetchServiceCost';
import useFeatureFlags from '@/hooks/useFeatureFlags';

export async function getServerSideProps(nextPageContext: NextPageContext) {
  const { req, query } = nextPageContext;
  const cookie: string = req?.headers?.cookie || '';
  const isIRWorkOrder = query[IR_WORK_ORDER_QUERY] === 'true';

  if (isIRWorkOrder) {
    const serviceReqStatusData = await fetchIRWorkOrderStatus(query.reqID as string, cookie);
    const { data: devices } = await getIRWorkOrders(cookie);
    const seletedIRDevice = getSelectedIRDevice(devices.data, query.reqID as string);
    seletedIRDevice.address = {
      ...seletedIRDevice,
      state: seletedIRDevice.stateOrProvince,
      pincode: seletedIRDevice.pincode,
    };
    return {
      props: {
        productsData: [],
        selectedDevice: seletedIRDevice,
        serviceReqHistoryData: { openRequests: [], closedRequests: [] },
        serviceReqStatusData,
        rescheduleFlow: query.reschedule ? true : '',
      },
    };
  }

  try {
    const assetsIRData = await fetchIRAndAssetsDetails(cookie);
    const { assets: productsData, installationRequests } = assetsIRData || {};
    const assetId = query.assetid ?? query.assetId;
    let selectedDevice = getSelectedDevice(productsData, assetId);

    let serviceReqHistoryData = {
      openRequests: [],
      closedRequests: [],
    };
    let serviceReqStatusData = {};

    serviceReqHistoryData = await serviceRequestHistory(selectedDevice.id, cookie);
    if (query.reqId && query.assetid) {
      serviceReqStatusData = await fetchServiceRequestStatus(
        query.assetid as string,
        selectedDevice?.accountId as string,
        query.reqId as string,
        cookie,
      );
    }

    return {
      props: {
        productsData,
        selectedDevice,
        serviceReqHistoryData,
        serviceReqStatusData,
        rescheduleFlow: query.reschedule ? true : '',
        assetId,
        installationRequests,
      },
    };
  } catch (error) {
    return ssrErrorHandler(error, nextPageContext);
  }
}

type serviceDataProps = {
  serviceReqHistoryData: reqHistoryType;
  productsData: AssetsData[];
  selectedDevice: AssetsData;
  benefits: BenefitsType[];
  price: number;
  statusCode: number;
  status: string;
  serviceReqStatusData: any;
  rescheduleFlow: true;
  assetId?: string;
  installationRequests?: any[];
};

export default function Service({
  serviceReqHistoryData,
  productsData,
  selectedDevice,
  statusCode,
  status,
  serviceReqStatusData,
  rescheduleFlow,
  assetId,
  installationRequests,
}: serviceDataProps) {
  const { state: serviceState, dispatch: serviceAction } = useContext(ServiceContext);
  const { state: serviceReqState, dispatch: serviceReqAction } = useContext(ServiceRequestContext);
  const { dispatch: productAction, state: productState } = useContext(ProductContext);
  const { currentComp } = serviceReqState;
  const { activeTab } = serviceState;
  const { query, push } = useRouter();
  const { triggerDefault } = useToast();
  const { webServiceGeoUpdate } = useFeatureFlags();

  const dispositions = useTriggerRequest({
    fetch: (...args: any) => fetchDispositions.apply(null, args),
  });
  const fetch = (...args: any) => serviceRequestHistory.apply(null, args);
  const { data, promisifyTrigger } = useTriggerRequest({ fetch });

  useOnMount(() => {
    if (assetId) {
      promisifyTrigger(assetId);
    }
  });

  useOnMount(() => {
    if (query.screen === 'issue_screen') {
      if (query.incidentType) {
        serviceAction.setActiveTab(tabItemNames.ServiceRequests);
        serviceReqAction.changeComponent('bookASlot', tabItemNames.MyDevices);
      } else {
        serviceAction.setActiveTab(tabItemNames.ServiceRequests);
        serviceReqAction.changeComponent('bookAService', tabItemNames.MyDevices);
      }
    }
  });

  const fetchInicidents = async () => {
    const {
      deviceId,
      expiryStatus: { status, daysToExpire },
      hubCode,
      isRental,
      isIrRequired,
    } = productState.selectedProduct;
    let expiryCohort = status;
    if (isRental && status !== DEVICE_STATUS.IW && status !== DEVICE_STATUS.IC)
      expiryCohort = DEVICE_STATUS.IC;
    return await dispositions.promisifyTrigger(
      deviceId,
      expiryCohort,
      hubCode,
      isIrRequired,
      daysToExpire,
    );
  };

  useEffect(() => {
    const handleWhatsAppJourney = async () => {
      try {
        const { serviceDate, serviceSlot } = date.convertStrToSlot(query.cpd as string);
        if (date.compareFirstDateEarlier(serviceSlot.slot.startTime, new Date())) {
          triggerDefault({
            message: 'The previous slot is no longer active. Kindly choose a future time.',
          });
          return;
        }

        if (
          query.screen === 'latlng' &&
          !webServiceGeoUpdate &&
          !productState?.selectedProduct?.address?.collectLatLong
        ) {
          triggerDefault({
            message:
              'Looks like your location is already recorded or location collection is turned off.',
          });
          return;
        }

        const promises = [
          fetchInicidents(),
          getServiceCost(productState.selectedProduct.id, query.incidentId as string),
        ];
        const [incidentsResponse, serviceCostResponse] = await Promise.all(promises);
        const { productId, materialId, amount, productName, isPrepaidAllowed } =
          serviceCostResponse;
        const matchedIncident: any = incidentsResponse?.data.find(
          (incident: any) => incident.id === query.incidentId,
        );
        if (matchedIncident) {
          serviceReqAction.updateSRContext({
            serviceDate: serviceDate,
            serviceSlot: serviceSlot,
            isPrepaidAllowed,
            materialId,
            productId,
            productName,
            servicePrice: amount,
            defectType: matchedIncident.name,
            incidentId: matchedIncident.id,
            serviceType: matchedIncident.jobType,
          });
        }

        if (query.screen === 'latlng') {
          serviceReqAction.changeComponent('mapLocator');
        } else if (query.screen === 'prepaid' && isPrepaidAllowed) {
          push({
            pathname: `/service/${productState.selectedProduct.id}/prepaid`,
            query: {
              schedulePrepaid: 'true',
            },
          });
        } else {
          serviceReqAction.changeComponent('bookAService', true);
        }
      } catch (error: any) {
        triggerDefault({
          message: error?.error ?? GENERIC_ERROR_MESSAGE,
        });
        return;
      }
    };

    if (
      (query.screen === 'latlng' || query.screen === 'prepaid') &&
      query.cpd &&
      query.incidentId
    ) {
      handleWhatsAppJourney();
    }
  }, []);

  useEffect(() => {
    serviceReqAction.setRescheduleFlow(rescheduleFlow);
    if (rescheduleFlow) {
      serviceReqAction.changeComponent('bookASlot');
      const isPastReq = checkIfPastReq(new Date(serviceReqStatusData?.slot?.start_time));
      const _serviceDate = isPastReq
        ? new Date()
        : new Date(serviceReqStatusData?.slot?.start_time);
      serviceReqAction.updateSRContext({
        serviceRequestID: serviceReqStatusData?.service_request_number,
        serviceType: serviceReqStatusData?.job_type,
        defectType: serviceReqStatusData?.defect_type ? serviceReqStatusData?.defect_type : null,
        address: serviceReqStatusData?.customer_address,
        serviceSlot: {
          slot: {
            startTime: serviceReqStatusData?.slot?.start_time,
            endTime: serviceReqStatusData?.slot?.end_time,
          },
        },
        addnRemarks: serviceReqStatusData?.additional_info
          ? serviceReqStatusData?.additional_info
          : null,
        serviceDate: _serviceDate,
        incidentId: selectedDevice?.incidentId,
        workOrderId: selectedDevice?.workOrderId,
      });
    }
  }, [rescheduleFlow, serviceReqStatusData]);

  useEffect(() => {
    const selectedItem = document.getElementById(activeTab);
    if (selectedItem) {
      selectedItem.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedDevice) {
      productAction.setProductsData(selectedDevice);

      serviceReqAction.updateSRContext({
        componentId: selectedDevice?.componentId,
        customerCode: selectedDevice?.customerCode,
      });
    }
    productAction.setProductsList(productsData);
    productAction.setIRDetails(installationRequests);
  }, [selectedDevice]);

  useEffect(() => {
    if (serviceReqHistoryData) {
      serviceReqAction.updateSRHistoryContext(transformServiceHistory(serviceReqHistoryData));
    } else {
      serviceReqAction.updateSRHistoryContext(null);
    }
  }, [serviceReqHistoryData]);

  useEffect(() => {
    if (productsData) {
      if (
        query.showMyDevices === 'true' ||
        (Array.isArray(query.showMyDevices) && query.showMyDevices[0] === 'true')
      ) {
        serviceAction.setActiveTab(tabItemNames.MyDevices);
      } else {
        serviceAction.setActiveTab(tabItemNames.ServiceRequests);
      }
    }
  }, [productsData]);

  function renderComponent() {
    switch (activeTab) {
      case tabItemNames.MyDevices:
        return (
          <MyDevices productsData={productsData} showBookServiceCta={data?.showBookServiceCta} />
        );
      case tabItemNames.ServiceRequests:
        return (
          <ServiceRequest
            productData={productsData}
            showBookServiceCta={data?.showBookServiceCta}
          />
        );
      default:
        break;
    }
  }

  if (statusCode) {
    return <Error statusCode={statusCode} message={status} />;
  }
  const isMapLocatorScreen = serviceReqState.currentComp.component === 'mapLocator';
  const isServiceSuccessScreen = serviceReqState.currentComp.component === 'bookServiceSuccess';
  return (
    <PageLayout
      hideFooter={
        currentComp.component !== 'pendingRequest' || tabItemNames.MyDevices === activeTab
      }
      mainClassName={classNames('min-h-screen pb-44 md:pb-56 bg-neutral-10', {
        'flex pt-16 mt-0 pb-20 md:!pb-0 items-center ': isServiceSuccessScreen,
        '!py-0': isMapLocatorScreen,
      })}
    >
      <Container
        className={classNames('mt-8', {
          '!mt-0': isServiceSuccessScreen || isMapLocatorScreen,
          '!px-0': isMapLocatorScreen,
        })}
      >
        {renderComponent()}
      </Container>
    </PageLayout>
  );
}
